import { useEditorClass } from '@tunasong/editor'
import { graphHooks } from '@tunasong/graph-lib/react'
import { Check as ResolveIcon } from '@tunasong/icons'
import type { EditorEvent, TunaPluginContentProps } from '@tunasong/plugin-lib'
import type { Comment, CoreElement, Entity, Persisted } from '@tunasong/schemas'
import { isPersisted } from '@tunasong/schemas'
import { Feed, ScrollContainer, IconButton, Tab, Tabs } from '@tunasong/ui-lib'
import { useCurrentUser } from '@tunasong/redux'
import type { FC } from 'react'
import { useRef, useState } from 'react'
import invariant from 'tiny-invariant'
import { ChatBox } from './chat-box.js'
import ChatFeed from './chat-feed.js'
import { useChat } from './chat.hook.js'
import { shouldPatchComment } from './should-patch-comment.js'
import cn from 'classnames'
import styles from './chat.module.css'

const ChatItemStyles = {
  fontSize: '14px !important',
  justifyContent: 'center',
}

export interface ChatProps extends TunaPluginContentProps<Entity> {
  className?: string
  showTabs?: boolean
  /** Forwarded to the ChatBox — see ChatBoxProps.pendingDraft. */
  pendingDraft?: string
  onDraftConsumed?: () => void
  /** Forwarded as the ChatBox container id (for aria-describedby wiring). */
  chatBoxId?: string
  onComment?(comment: Comment): void
}

export const Chat: FC<ChatProps> = props => {
  const { element, className, onComment, showTabs = true, pendingDraft, onDraftConsumed, chatBoxId } = props
  const updateEntity = graphHooks.useEntityUpdate()

  const ChatEditor = useEditorClass('ChatEditor')

  const { userId } = useCurrentUser()

  const { activeComments, resolvedComments, addComment, resolveComment, getNext } = useChat({
    element,
    pageSize: 10,
  })
  const handleResolve = (c: Comment) => () => resolveComment(c)

  const commentTemplate = element?.id
    ? ({
        type: 'comment',
        parentId: element.id,
        userId: userId ?? 'unknown',
        children: [{ type: 'line', children: [{ text: '' }] }],
      } satisfies CoreElement<Comment>)
    : null

  const commentRef = useRef<Comment | null>(commentTemplate)

  /**
   * GH #1280: gate PATCH on a real content edit. Plate's onValueChange
   * fires on initial normalization and on every prop-driven editor
   * re-create, which previously produced a phantom PATCH on every
   * rendered comment — and a 404-racing PATCH against the just-POSTed
   * comment. Updates may legitimately occur even when the editor is
   * readOnly (e.g. todo checkboxes nested inside the comment), so the
   * decision is content-based, not readOnly-based.
   */
  const handleCommentUpdate = (original: Persisted<Comment>) => (e: CoreElement) => {
    if (!shouldPatchComment(original, e)) {
      return
    }
    updateEntity(e.id, e)
  }

  const createFeedItems = (feedComments: Persisted<Comment>[] = []) =>
    feedComments?.map(c => ({
      ...c,
      content: (
        <>
          <div className={styles.feedItemContent}>
            {/* @todo this will flicker when receiving updates. Can we render this offline? */}
            <ChatEditor
              style={ChatItemStyles}
              readOnly={true}
              autoFocus={false}
              element={c}
              onChange={handleCommentUpdate(c)}
            />
          </div>
        </>
      ),
      actions:
        showTabs && isPersisted(c) && showTabs && !c.resolved && c.userId === userId ? (
          <IconButton className={styles.resolveButton} onClick={handleResolve(c)}>
            <ResolveIcon />
          </IconButton>
        ) : undefined,
    })) ?? []

  /**
   * comment state is set to the comment object
   */
  /** We keep a counter, so that we ignore pending comments when the expected count is at what we expect */
  const expectedCommentCount = useRef(0)

  const handleAddComment = async (ev: EditorEvent) => {
    ev.preventDefault()
    const comment = commentRef.current
    invariant(comment, 'Comment should be set')

    expectedCommentCount.current = activeComments.length + 1

    /** @todo The problem here is that the update from server side comes before this code is executed */
    const createdComment = await addComment(comment)

    if (onComment && createdComment) {
      onComment(createdComment)
    }
    commentRef.current = commentTemplate
  }

  const comments = createFeedItems(activeComments)
  const resolved = createFeedItems(resolvedComments)

  const handleComment = (c: Comment) => (commentRef.current = c)

  const [tab, setTab] = useState(0)
  const handleTab = (_ev: React.SyntheticEvent | null, num: string | number) => setTab(num as number)
  // we use state to trigger a re-render since we need topRef to be set for ScrollContainer
  const [topEl, setTopEl] = useState<HTMLDivElement | null>(null)

  return (
    <div className={cn(styles.container, className)}>
      {showTabs && (
        <Tabs value={tab} onChange={handleTab}>
          <Tab label={`Active`} />
          <Tab label={`Resolved (${resolved.length})`} />
        </Tabs>
      )}

      {tab === 0 && (
        <>
          <ScrollContainer onUp={getNext} topEl={topEl}>
            <ChatFeed items={comments} topRef={setTopEl} />
          </ScrollContainer>
          {commentRef.current ? (
            <ChatBox
              onChange={handleComment}
              onComplete={handleAddComment}
              pendingDraft={pendingDraft}
              onDraftConsumed={onDraftConsumed}
              id={chatBoxId}
              aria-describedby={chatBoxId ? `${chatBoxId}-role-description` : undefined}
            />
          ) : null}
        </>
      )}

      {tab === 1 ? <Feed items={resolved} className={styles.resolvedFeed} /> : null}
    </div>
  )
}
